import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CircularProgress, Checkbox, FormControlLabel, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import StripeCDKSavedCards from './StripeCDKSavedCards';

// 导入支付卡图标
// import genericCardIcon from '../../images/payment/generic-card.svg';
import { getCardBrandIcon } from '../../utils/cardUtils';
// 导入测试工具
import StripeTestUtil from '../../utils/stripeTestUtil';
import { getCurCurrencyOfUnit } from "../../utils/util";
import { safeFetchJson } from '../../utils/prerender';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: '20px 24px',
    position: 'relative', // 为关闭按钮提供相对定位上下文
    '& label': {
      color: '#cdccd0',
      marginBottom: '8px',
      display: 'block',
      fontSize: '14px'
    }
  },
  closeButton: {
    position: 'absolute',
    right: '10px',
    top: '-10px', // 将按钮位置向上移动，原来是 10px
    color: '#8a8794',
    cursor: 'pointer',
    zIndex: 10,
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(138, 135, 148, 0.1)',
      color: '#ffffff'
    }
  },
  cardContainer: {
    position: 'relative',
    marginBottom: '25px',
    border: '1px solid #3a3643',
    borderRadius: '12px',
    padding: '16px',
    backgroundColor: '#211c27',
    transition: 'all 0.3s ease'
  },
  cardFocused: {
    borderColor: '#6c63ff',
    boxShadow: '0 0 0 2px rgba(108, 99, 255, 0.2)'
  },
  cardError: {
    borderColor: '#ff6b6b'
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    '& .card-icon': {
      width: '40px',
      marginRight: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  },
  fieldRow: {
    display: 'flex',
    gap: '15px',
    marginTop: '20px'
  },
  cardNumberLabel: {
    color: '#cdccd0',
    fontSize: '14px',
    marginBottom: '8px'
  },
  cardFieldContainer: {
    flex: '1',
    '& .StripeElement': {
      padding: '12px 15px',
      borderRadius: '8px',
      backgroundColor: '#3a3643',
      color: 'white',
      fontSize: '16px',
      '&--focus': {
        boxShadow: '0 0 0 2px rgba(108, 99, 255, 0.2)'
      },
      '&--invalid': {
        borderColor: '#ff6b6b'
      }
    }
  },
  halfWidth: {
    width: '50%'
  },
  saveCardOption: {
    marginTop: '15px',
    color: '#cdccd0',
    '& .MuiFormControlLabel-label': {
      fontSize: '14px'
    }
  },
  submitButton: {
    marginTop: '25px',
    backgroundColor: '#6c63ff',
    color: 'white',
    padding: '10px 25px',
    borderRadius: '30px',
    '&:hover': {
      backgroundColor: '#5a52d5'
    },
    '&:disabled': {
      backgroundColor: '#3a3643',
      color: '#8a8794'
    }
  },
  errorMessage: {
    color: '#ff6b6b',
    marginTop: '10px',
    fontSize: '14px'
  },
  successMessage: {
    color: '#4caf50',
    marginTop: '10px',
    fontSize: '14px'
  },
  cardPreview: {
    height: '180px',
    borderRadius: '15px',
    padding: '20px',
    position: 'relative',
    background: 'linear-gradient(135deg, #3a3643 0%, #211c27 100%)',
    marginBottom: '25px',
    overflow: 'hidden',
    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  cardChip: {
    width: '40px',
    height: '30px',
    backgroundColor: '#FFD700',
    borderRadius: '5px',
    marginBottom: '25px'
  },
  cardNumberPreview: {
    fontSize: '18px',
    letterSpacing: '2px',
    color: 'white',
    marginBottom: '20px'
  },
  cardHolderDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#cdccd0'
  },
  cardBrand: {
    position: 'absolute',
    right: '20px',
    top: '20px',
    width: '60px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px'
  },
  savedCards: {
    marginBottom: '20px'
  },
  savedCardOption: {
    padding: '15px',
    border: '1px solid #3a3643',
    borderRadius: '12px',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(58, 54, 67, 0.5)'
    }
  },
  selectedCard: {
    borderColor: '#6c63ff',
    backgroundColor: 'rgba(108, 99, 255, 0.1)'
  },
  newCardOption: {
    display: 'flex',
    alignItems: 'center',
    padding: '15px',
    border: '1px solid #3a3643',
    borderRadius: '12px',
    marginBottom: '20px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(58, 54, 67, 0.5)'
    }
  },
  addIcon: {
    marginRight: '10px',
    color: '#6c63ff',
    fontSize: '24px'
  },
  orderSummary: {
    marginTop: '30px',
    padding: '15px',
    backgroundColor: '#211c27',
    borderRadius: '12px',
    border: '1px solid #3a3643'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    fontSize: '14px',
    color: '#cdccd0'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '15px',
    paddingTop: '15px',
    borderTop: '1px solid #3a3643',
    fontSize: '16px',
    fontWeight: 'bold',
    color: 'white'
  },
  termsCheckbox: {
    marginTop: '15px',
    color: '#cdccd0',
    '& .MuiFormControlLabel-label': {
      fontSize: '14px'
    }
  },
  termsLink: {
    color: '#6c63ff',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  paymentStatus: {
    marginTop: '15px',
    padding: '10px',
    borderRadius: '8px',
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      borderRadius: 0,
      backgroundColor: '#211c27',
      padding: theme.spacing(1)
    }
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  errorContainer: {
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    borderRadius: '4px',
    borderLeft: '3px solid #ff6b6b'
  },
  successContainer: {
    padding: '8px 12px',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    borderRadius: '4px',
    borderLeft: '3px solid #4caf50'
  },
  mobileFormField: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1)
    }
  },
  cardFieldErrorText: {
    color: '#ff6b6b',
    fontSize: '12px',
    marginTop: '4px'
  },
  secureInfo: {
    fontSize: '12px',
    color: '#8a8794',
    textAlign: 'center',
    marginTop: '15px'
  }
}));

// Define the SCA handling switch (use environment variable in real app)
// For this example, we'll define it directly. Set to 'true' to enable, 'false' to disable.
// In a Create React App project, you would use process.env.REACT_APP_ENABLE_SCA_HANDLING === 'true'
const ENABLE_SCA_HANDLING = true; // Or false for testing disabled flow

const StripeCDKCheckoutForm = ({
  user,
  onSuccess,
  onError,
  authToken,
  onClose, // 添加关闭按钮的回调属性
  setProcessingState = () => { }, // Default empty function
  onRequiresAction = () => { }, // Add a new callback for requires_action state
  order
}) => {
  const classes = useStyles();
  const stripe = useStripe();
  const elements = useElements();

  // 创建一个 ref 来跟踪组件是否已挂载
  const isMounted = useRef(true);

  const [nameOnCard, setNameOnCard] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardNumberFocused, setCardNumberFocused] = useState(false);
  const [cardExpiryFocused, setCardExpiryFocused] = useState(false);
  const [cardCvcFocused, setCardCvcFocused] = useState(false);
  const [cardNumberError, setCardNumberError] = useState(false);
  const [cardExpiryError, setCardExpiryError] = useState(false);
  const [cardCvcError, setCardCvcError] = useState(false);
  const [cardBrand, setCardBrand] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(true);

  // 显示的卡号，不是实际卡号
  const [displayCardNumber, setDisplayCardNumber] = useState('•••• •••• •••• ••••');
  const [displayCardExpiry, setDisplayCardExpiry] = useState('••/••');

  // 替换模拟数据，使用实际数据
  const [savedCards, setSavedCards] = useState([]);
  const [loadingSavedCards, setLoadingSavedCards] = useState(false);

  const amount = order?.balance || 0;
  const currency = order?.currency;
  const productId = order?.product;
  const quantity = order?.amount || 0;
  const purchaseMethod = 'stripe';
  const giftEmail = order?.purchaseType === 'g' ? order.sendEmail : user?.email;
  const price = order?.salePrice;

  // 添加测试相关状态
  //const [testEnabled,setTestEnabled] = useState(StripeTestUtil.isTestMode());

  // 创建测试面板组件
  const TestPanel = useMemo(() => {
    if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
      return null;
    }
    return StripeTestUtil.createTestPanel(React);
  }, []);

  // 检查测试模式状态
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'staging') {
      //setTestEnabled(StripeTestUtil.isTestMode());
    }
    console.log(order);
  }, []);

  // 在组件挂载时获取已保存的卡片
  useEffect(() => {
    if (authToken) {
      fetchSavedCards();
    }
  }, [authToken]);

  // 获取已保存卡片的函数
  const fetchSavedCards = async () => {
    if (!isMounted.current) return; // 如果组件已卸载，则不执行
    console.log('[StripeCDKCheckoutForm] Fetching saved cards...');
    setLoadingSavedCards(true);
    try {
      const data = await safeFetchJson('/api/v1/user/payment-methods', { method: 'GET', headers: { 'Authorization': `JWT ${authToken}`, 'Content-Type': 'application/json' } });
      console.log('[StripeCDKCheckoutForm] Fetched saved cards data (or null in prerender):', data);
      if (data && isMounted.current) { // 再次检查，以防在 await response.json() 期间卸载
        // 使用测试工具准备卡列表
        const cards = StripeTestUtil.isTestMode()
          ? StripeTestUtil.prepareSavedCardTest(data.paymentMethods || [])
          : (data.paymentMethods || []);

        setSavedCards(cards);
        const defaultCard = cards.find(card => card.isDefault);
        if (defaultCard) {
          setSelectedCard(defaultCard.id);
          setShowNewCardForm(false);
          console.log('[StripeCDKCheckoutForm] Default card selected:', defaultCard.id, 'showNewCardForm:', false);
        } else {
          setShowNewCardForm(cards.length === 0);
          console.log('[StripeCDKCheckoutForm] No default card. showNewCardForm:', cards.length === 0);
        }
      } else {
        // data is null or component unmounted; provide a safe fallback
        if (isMounted.current) {
          if (StripeTestUtil.isTestMode()) {
            const testCards = StripeTestUtil.prepareSavedCardTest([]);
            setSavedCards(testCards);
            if (testCards.length > 0) {
              setSelectedCard(testCards[0].id);
              setShowNewCardForm(false);
            }
          } else {
            setSavedCards([]);
          }
        }
      }
    } catch (error) {
      console.error('[StripeCDKCheckoutForm] Error fetching saved cards:', error);
      if (isMounted.current) {
        // 如果在测试模式下，添加测试卡
        if (StripeTestUtil.isTestMode()) {
          const testCards = StripeTestUtil.prepareSavedCardTest([]);
          setSavedCards(testCards);
          if (testCards.length > 0) {
            setSelectedCard(testCards[0].id);
            setShowNewCardForm(false);
          }
        } else {
          setSavedCards([]);
        }
      }
    } finally {
      if (isMounted.current) {
        setLoadingSavedCards(false);
        console.log('[StripeCDKCheckoutForm] Finished fetching saved cards.');
      }
    }
  };

  const [paymentStatus, setPaymentStatus] = useState({
    stage: 'initial', // initial, processing, creating_intent, confirming, handling_action, success, failed
    message: '',
    details: null
  });

  // useEffect 清理函数，在组件卸载时将 isMounted.current 设置为 false
  useEffect(() => {
    isMounted.current = true; // 组件挂载时设置为 true
    return () => {
      isMounted.current = false; // 组件卸载时设置为 false
    };
  }, []); // 空依赖数组确保这个 effect 只在挂载和卸载时运行

  // 卡号输入组件的样式选项
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: 'white',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        '::placeholder': {
          color: '#8a8794',
        },
        iconColor: 'white', // 确保图标显示为白色
        lineHeight: '40px', // 增加行高以便更容易点击和聚焦
        ':-webkit-autofill': {
          color: 'white',
        },
      },
      invalid: {
        color: '#ff6b6b',
        iconColor: '#ff6b6b', // 错误状态下图标为红色
      },
    },
    iconStyle: 'solid', // 设置为solid确保图标显示
    hidePostalCode: true, // 不需要邮政编码时
  };

  // 添加状态显示组件(在组件内部添加)
  const PaymentStatusIndicator = ({ status }) => {
    const getStatusDisplay = () => {
      switch (status.stage) {
        case 'processing':
        case 'confirming':
        case 'creating_intent': // 添加 creating_intent 以显示一致的加载状态
        case 'handling_action': // 添加 handling_action
          return (
            <div className={classes.statusContainer}>
              <CircularProgress size={20} />
              <Typography variant="body2">{status.message || 'Processing...'}</Typography>
            </div>
          );
        case 'failed':
          return (
            <div className={classes.errorContainer}>
              <Typography variant="body2" color="error">{status.message}</Typography>
            </div>
          );
        case 'success':
          return (
            <div className={classes.successContainer}>
              <Typography variant="body2" style={{ color: '#4caf50' }}>{status.message}</Typography>
            </div>
          );
        case 'captcha_required': // 新增状态
          return (
            <div className={classes.statusContainer}> {/* 可以考虑使用不同的样式 */}
              <Typography variant="body2">{status.message || 'Please complete the CAPTCHA to continue.'}</Typography>
              {/* Stripe 通常会自动处理验证码的显示，但你可以提供一个占位符或额外提示 */}
              {/* <div id="stripe-captcha-container"></div> */}
            </div>
          );
        default:
          return null;
      }
    };

    return status.stage !== 'initial' ? (
      <div className={classes.paymentStatus}>
        {getStatusDisplay()}
      </div>
    ) : null;
  };
  // 添加表单验证函数
  const validateForm = () => {
    const errors = {};

    if (!nameOnCard.trim()) {
      errors.name = 'Please enter the cardholder\'s name';
    }

    if (!cardNumberComplete) {
      errors.cardNumber = 'Please enter a valid card number';
    }

    if (!cardExpiryComplete) {
      errors.expiry = 'Please enter a valid expiry date';
    }

    if (!cardCvcComplete) {
      errors.cvc = 'Please enter a valid security code';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // 表单验证
    if (!agreeToTerms) {
      setCheckoutError('Please agree to the terms of service and privacy policy');
      setPaymentStatus({ stage: 'failed', message: 'Please agree to the terms of service and privacy policy' });
      return;
    }


    // 表单验证
    // if (!agreeToTerms) {
    //   setCheckoutError('请同意服务条款和隐私政策');
    //   return;
    // }

    if (selectedCard) {
      // 使用已保存的卡进行支付，不需要表单认证
      handlePaymentWithSavedCard();
    } else {
      // 如果是新卡进行表单认证
      const formErrors = validateForm();
      if (Object.keys(formErrors).length > 0) {
        const errorMessages = Object.values(formErrors).join(', ');
        setCheckoutError(errorMessages);
        return;
      }

      // 使用新卡支付
      handlePaymentWithNewCard();
    }
  };

  const handlePaymentWithSavedCard = async () => {
    if (!selectedCard) return;

    setIsProcessing(true);
    setCheckoutError('');
    setPaymentStatus({ stage: 'processing', message: 'Using saved card for payment...' });

    if (setProcessingState) {
      setProcessingState(true);
    }

    try {
      // 测试模式下检查是否需要模拟离线会话失败
      const mockFailure = StripeTestUtil.isTestMode() && localStorage.getItem('stripe_mock_off_session_failure') === 'true';
      if (mockFailure && selectedCard.includes('test_pm')) {
        // 模拟卡被拒绝的情况
        throw new Error('card_declined: 模拟卡被拒绝的情况，将触发优雅降级');
      }

      // 首先尝试使用离线会话模式（off_session=true）支付 - 最佳用户体验
      // 调用后端API使用已保存的卡进行支付
      const backendResponseData = await safeFetchJson('/api/v1/user/cd-key/process-payment', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `JWT ${authToken}` }, body: JSON.stringify({ productId, amount, currency, quantity, purchaseMethod, giftEmail, price, orderId: order.id, paymentMethodId: selectedCard, saveCard: false }) });
      if (!backendResponseData) {
        throw new Error('Using saved card payment failed (unavailable during prerender)');
      }
      const clientSecret = backendResponseData.clientSecret;
      const paymentIntentStatus = backendResponseData.paymentIntentStatus; // <-- 获取状态
      const orderData = backendResponseData.orderData || backendResponseData; // <-- 获取订单数据

      if (paymentIntentStatus === 'succeeded') {
        if (isMounted.current) { // <--- 检查组件是否挂载
          console.log('Payment with saved card already succeeded on backend.');
          const successMsg = getSuccessMessage(orderData); // 使用完整的订单数据
          setPaymentStatus({ stage: 'success', message: successMsg });
          setCheckoutSuccess(successMsg);
          onSuccess({ ...orderData, paymentIntent: { status: 'succeeded', id: backendResponseData.stripePaymentIntentId } }); // 确保传递必要信息
        }
      } else if (clientSecret && (paymentIntentStatus === 'requires_payment_method' || paymentIntentStatus === 'requires_confirmation' || paymentIntentStatus === 'processing')) {
        // 需要客户端确认 (例如，如果后端没有设置 confirm: true, 或者处于中间状态)
        if (isMounted.current) { // <--- 检查
          console.log(`PaymentIntent status is ${paymentIntentStatus}, proceeding with client-side confirmation.`);
          setPaymentStatus({ stage: 'confirming', message: 'Confirming your payment...' });
        }
        const confirmResult = await stripe.confirmCardPayment(clientSecret);

        if (isMounted.current) { // <--- 在所有 await 之后、更新状态之前再次检查
          if (confirmResult.error) {
            // SCA 或其他确认错误
            if (confirmResult.error.code === 'payment_intent_authentication_failure' ||
              (confirmResult.paymentIntent && confirmResult.paymentIntent.status === 'requires_action')) {
              if (isMounted.current) {
                console.log('Payment requires action (SCA/3D Secure) after saved card confirmation.');
                setPaymentStatus({ stage: 'handling_action', message: 'Additional verification required, please follow the prompts...' });
              }
              if (onRequiresAction) onRequiresAction(confirmResult.paymentIntent);

              if (ENABLE_SCA_HANDLING) {
                const actionResult = await stripe.handleCardAction(clientSecret);
                if (isMounted.current) {
                  if (actionResult.error) {
                    throw actionResult.error; // 抛出以便被下面的 catch 捕获
                  } else if (actionResult.paymentIntent && actionResult.paymentIntent.status === 'succeeded') {
                    const successMsg = getSuccessMessage(orderData);
                    setPaymentStatus({ stage: 'success', message: successMsg });
                    setCheckoutSuccess(successMsg);
                    onSuccess({ ...orderData, ...actionResult.paymentIntent });
                  } else {
                    throw new Error(`SCA payment status not successful: ${actionResult.paymentIntent?.status || 'unknown'}`);
                  }
                }
              } else {
                throw new Error('Payment requires SCA but handling is disabled.');
              }
            } else {
              console.log('confirmError', confirmResult.error);
              throw confirmResult.error; // 抛出以便被下面的 catch 捕获
            }
          } else if (confirmResult.paymentIntent && confirmResult.paymentIntent.status === 'succeeded') {
            // 客户端确认成功
            const successMsg = getSuccessMessage(orderData);
            setPaymentStatus({ stage: 'success', message: successMsg });
            setCheckoutSuccess(successMsg);
            onSuccess({ ...orderData, ...confirmResult.paymentIntent });
          } else {
            throw new Error(`Payment processing exception: Client-side confirmation failed. Unexpected Status: ${confirmResult.paymentIntent?.status || 'unknown'}`);
          }
        }
      } else if (paymentIntentStatus === 'requires_action') {
        // 通常由后端直接创建PI时（未confirm）且需要SCA时触发
        if (isMounted.current) { // <--- 检查
          console.log('Payment requires action (SCA/3D Secure) based on backend PI status.');
          setPaymentStatus({ stage: 'handling_action', message: 'Additional verification required, please follow the prompts...' });
        }
        if (onRequiresAction) onRequiresAction({ client_secret: clientSecret, status: 'requires_action' }); // 通知父组件

        if (ENABLE_SCA_HANDLING) {
          const actionResult = await stripe.handleCardAction(clientSecret);
          if (isMounted.current) { // <--- 检查
            if (actionResult.error) {
              throw actionResult.error;
            } else if (actionResult.paymentIntent && actionResult.paymentIntent.status === 'succeeded') {
              const successMsg = getSuccessMessage(orderData);
              setPaymentStatus({ stage: 'success', message: successMsg });
              setCheckoutSuccess(successMsg);
              if (onSuccess) onSuccess({ ...orderData, ...actionResult.paymentIntent });
            } else {
              throw new Error(`SCA payment status not successful: ${actionResult.paymentIntent?.status || 'unknown'}`);
            }
          }
        } else {
          throw new Error('Payment requires SCA but handling is disabled.');
        }
      } else if (backendResponseData.success && !clientSecret) {

        if (isMounted.current) { // <--- 检查
          console.warn('Payment reported as success by backend without clientSecret for confirmation, potentially legacy flow.');
          const successMsg = getSuccessMessage(orderData);
          setPaymentStatus({ stage: 'success', message: successMsg });
          setCheckoutSuccess(successMsg);
          onSuccess(orderData);
        }
      } else {
        // 其他未知情况
        if (isMounted.current) {
          throw new Error(`Payment processing exception: Unknown backend response status (${paymentIntentStatus}) or missing clientSecret.`);
        }
      }
    } catch (error) {
      if (isMounted.current) { // <--- 检查
        console.error('Error paying with saved card:', error);

        // 判断是否是卡被拒或需要验证的特定错误
        let isCardDeclinedError = error.code === 'card_declined' ||
          (error.message && error.message.includes('card')) ||
          (error.message && error.message.includes('被拒绝'));

        let isAuthError = error.code === 'authentication_required' ||
          (error.message && error.message.includes('verification')) ||
          (error.message && error.message.includes('验证'));
        isCardDeclinedError = false;
        isAuthError = false;
        // 如果是卡被拒或需要验证，尝试交互式支付
        if (isCardDeclinedError || isAuthError) {
          console.log('Saved card was declined or requires authentication. Trying interactive payment mode...');

          if (isMounted.current) {
            setPaymentStatus({
              stage: 'processing',
              message: 'Your card requires additional verification. Switching to interactive mode...'
            });
          }

          try {
            // 使用交互式模式再次尝试支付
            const interactiveData = await safeFetchJson('/api/v1/user/cd-key/process-payment', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `JWT ${authToken}` }, body: JSON.stringify({ productId, amount, currency, quantity, purchaseMethod, giftEmail, price, orderId: order.id, paymentMethodId: selectedCard, saveCard: false, interactive_mode: true }) });
            if (!interactiveData) throw new Error('Interactive payment mode failed (unavailable during prerender)');
            const interactiveClientSecret = interactiveData.clientSecret;
            const interactiveStatus = interactiveData.paymentIntentStatus;
            const orderData = interactiveData.orderData || interactiveData;

            if (interactiveStatus === 'succeeded') {
              // 交互式模式下支付已经成功
              if (isMounted.current) {
                const successMsg = getSuccessMessage(orderData);
                setPaymentStatus({ stage: 'success', message: successMsg });
                setCheckoutSuccess(successMsg);
                onSuccess({ ...orderData, paymentIntent: { status: 'succeeded', id: interactiveData.stripePaymentIntentId } });
              }
            } else if (interactiveClientSecret) {
              // 需要前端确认或处理SCA
              if (isMounted.current) {
                setPaymentStatus({ stage: 'confirming', message: 'Please confirm your payment...' });
              }

              // 手动引导用户确认支付
              const confirmResult = await stripe.confirmCardPayment(interactiveClientSecret, {
                payment_method: selectedCard
              });

              if (isMounted.current) {
                if (confirmResult.error) {
                  throw confirmResult.error;
                } else if (confirmResult.paymentIntent && confirmResult.paymentIntent.status === 'succeeded') {
                  const successMsg = getSuccessMessage(orderData);
                  setPaymentStatus({ stage: 'success', message: successMsg });
                  setCheckoutSuccess(successMsg);
                  onSuccess({ ...orderData, ...confirmResult.paymentIntent });
                } else if (confirmResult.paymentIntent && confirmResult.paymentIntent.status === 'requires_action') {
                  // 处理SCA
                  setPaymentStatus({ stage: 'handling_action', message: 'Additional verification required...' });
                  if (onRequiresAction) onRequiresAction(confirmResult.paymentIntent);

                  if (ENABLE_SCA_HANDLING) {
                    const actionResult = await stripe.handleCardAction(interactiveClientSecret);
                    if (isMounted.current) {
                      if (actionResult.error) {
                        throw actionResult.error;
                      } else if (actionResult.paymentIntent && actionResult.paymentIntent.status === 'succeeded') {
                        const successMsg = getSuccessMessage(orderData);
                        setPaymentStatus({ stage: 'success', message: successMsg });
                        setCheckoutSuccess(successMsg);
                        onSuccess({ ...orderData, ...actionResult.paymentIntent });
                      } else {
                        throw new Error(`SCA payment status not successful in interactive mode: ${actionResult.paymentIntent?.status || 'unknown'}`);
                      }
                    }
                  } else {
                    throw new Error('Payment requires SCA but handling is disabled in interactive mode.');
                  }
                } else {
                  throw new Error(`Unexpected payment status in interactive mode: ${confirmResult.paymentIntent?.status || 'unknown'}`);
                }
              }
            } else {
              throw new Error('Missing client secret for interactive payment mode.');
            }
          } catch (interactiveError) {
            // 如果交互式模式也失败，则抛出这个错误
            if (isMounted.current) {
              console.error('Interactive payment mode also failed:', interactiveError);
              const detailedError = interactiveError.message || 'Payment failed even in interactive mode';
              const errorMessage = detailedError || 'Payment failed even in interactive mode';
              setPaymentStatus({ stage: 'failed', message: errorMessage });
              setCheckoutError(errorMessage);
              if (onError) onError(interactiveError);
            }
          }
        } else {
          // 不是卡被拒或验证错误，直接显示原始错误
          const errorMessage = error.response ?
            (await error.response.json()).message :
            (error.message || 'Payment processing error');

          const detailedError = error.message || (error.response && await error.response.text()) || 'An error occurred during payment processing, please try again';
          setPaymentStatus({ stage: 'failed', message: errorMessage || detailedError });
          setCheckoutError(errorMessage || detailedError);
          if (onError) onError(error);
        }
      }
    } finally {
      if (isMounted.current) { // <--- 检查
        setIsProcessing(false);
        if (setProcessingState) {
          setProcessingState(false);
        }
      }
    }
  };

  const handlePaymentWithNewCard = async () => {
    setIsProcessing(true);
    setCheckoutError('');
    setPaymentStatus({ stage: 'processing', message: 'Creating payment information...' });
    // 通知父组件处理状态
    if (setProcessingState) {
      setProcessingState(true);
    }
    try {
      // 检查是否需要保存卡片
      if (saveCard) {
        // 先检查已保存的卡数量是否达到上限（3张）
        if (savedCards.length >= 3) {
          // 已达到保存上限，告知用户但仍然继续使用此卡支付（不保存）
          console.log('Maximum number of saved cards (3) reached. Cannot save another card.');
          setPaymentStatus({
            stage: 'processing',
            message: 'You have reached the maximum of 3 saved cards. Using this card for payment only.'
          });

          // 将saveCard设为false，这样本次支付不会保存卡片
          setSaveCard(false);

          // 可以选择显示错误消息，但不阻止支付流程
          setTimeout(() => {
            if (isMounted.current) {
              setCheckoutError('Maximum of 3 saved cards reached. Please delete an existing card before saving a new one.');
            }
          }, 1000);

          // 注意：这里不直接返回，继续正常支付流程
        }

        // 获取卡片基本信息，用于检查是否重复
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          throw new Error('Card element not found');
        }

        // 获取卡片信息
        const { brand, last4 } = await cardElement.card || {};

        // 简化判断：只要卡号last4和品牌相同，就认为是同一张卡
        const isCardAlreadySaved = savedCards.some(card =>
          card.last4 === last4 &&
          card.brand.toLowerCase() === (brand || '').toLowerCase()
        );

        if (isCardAlreadySaved) {
          // 找到匹配的卡片
          const existingCard = savedCards.find(card =>
            card.last4 === last4 &&
            card.brand.toLowerCase() === (brand || '').toLowerCase()
          );

          // 如果找到了已存在的卡，使用它而不是创建新卡
          if (existingCard) {
            console.log('Card already saved, using existing payment method:', existingCard.id);
            setPaymentStatus({ stage: 'processing', message: 'Using your saved card...' });

            // 使用现有的支付方式
            await processPayment(existingCard.id, false); // false表示不保存卡(因为已经保存过了)
            return;
          }
        }
      }

      // 1. Create PaymentMethod
      const { error: createPaymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: nameOnCard,
          // Consider adding email here if not sending in processPayment
          // email: purchaseMethod === 'g' ? giftEmail : user?.email,
        },
      });

      if (createPaymentMethodError) {
        if (isMounted.current) { // <--- 检查
          setPaymentStatus({
            stage: 'failed',
            message: createPaymentMethodError.message || 'Card information processing error, please check your card details'
          });
        }
        // No need to throw here, let finally handle processing state
        return; // Exit early
      }

      if (isMounted.current) { // <--- 检查
        setPaymentStatus({ stage: 'creating_intent', message: 'Security processing your request...' });
      }

      // 2. Call backend to create PaymentIntent and attempt payment confirmation
      // const paymentResponse = await processPayment(paymentMethod.id);
      console.log('[StripeCDKCheckoutForm] Calling processPayment for new card. PaymentMethod ID:', paymentMethod.id, 'Save card choice:', saveCard);
      await processPayment(paymentMethod.id, saveCard);

    } catch (error) {
      // Catch errors *not* handled within processPayment (e.g., network errors calling backend)
      if (isMounted.current) { // <--- 检查
        console.error('Payment processing top-level error:', error);
        const errorMsg = error.message || (error.error && error.error.message) || 'An error occurred during payment processing, please try again';
        setPaymentStatus({
          stage: 'failed',
          message: errorMsg
        });
        setCheckoutError(errorMsg);
        if (onError) onError(error); // Notify parent component
      }
    } finally {
      if (isMounted.current) { // <--- 检查
        setIsProcessing(false);
        // Notify parent component processing has finished
        if (setProcessingState) {
          setProcessingState(false);
        }
      }
    }
  };

  const processPayment = async (paymentMethodId, shouldSaveCard = false) => {
    let clientSecret = null;
    let orderData = null; // 用于存储后端返回的完整订单信息
    let initialPaymentIntentStatus = null; // 用于存储后端返回的PI初始状态

    try {
      // ----- Step 1: Call Backend to Create Payment Intent -----
      setPaymentStatus({ stage: 'creating_intent', message: 'Creating secure payment session...' });
      console.log('发起支付请求到后端 (新卡):', { productId, amount, currency, quantity, purchaseMethod, giftEmail, orderId: order.id, payType: 'stripe', paymentMethodId, saveCard: shouldSaveCard });

      const backendResponseData = await safeFetchJson('/api/v1/user/cd-key/process-payment', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `JWT ${authToken}` }, body: JSON.stringify({ productId, amount, currency, quantity, purchaseMethod, giftEmail, price, orderId: order.id, payType: 'stripe', paymentMethodId, saveCard: shouldSaveCard }) });
      if (!backendResponseData) {
        if (isMounted.current) {
          const backendErrorMessage = 'Failed to create payment request (unavailable during prerender)';
          setPaymentStatus({ stage: 'failed', message: backendErrorMessage });
          setCheckoutError(backendErrorMessage);
          if (onError) onError(new Error(backendErrorMessage));
        }
        return;
      }
      console.log('后端创建 PaymentIntent 响应 (新卡):', backendResponseData);
      clientSecret = backendResponseData.clientSecret;
      orderData = backendResponseData.orderData || backendResponseData; // 获取完整的订单数据
      initialPaymentIntentStatus = backendResponseData.paymentIntentStatus; // 获取初始状态

      if (!clientSecret) {
        // 如果后端没有在这一步返回 clientSecret，但返回了成功状态（不太可能，但作为防御）
        if (initialPaymentIntentStatus === 'succeeded') {
          if (isMounted.current) { // <--- 检查
            console.log('Payment (new card) succeeded directly on backend (no clientSecret returned for confirmation).');
            const successMsg = getSuccessMessage(orderData);
            setPaymentStatus({ stage: 'success', message: successMsg });
            setCheckoutSuccess(successMsg);
            if (onSuccess) onSuccess({ ...orderData, paymentIntent: { status: 'succeeded', id: backendResponseData.stripePaymentIntentId } });
          }
          return;
        }
        throw new Error('Backend response is missing clientSecret and status is not successful');
      }

      // ----- Step 2: Handle based on initial PaymentIntent Status from backend -----
      if (initialPaymentIntentStatus === 'succeeded') {
        if (isMounted.current) { // <--- 检查
          // 理论上，新卡创建PI时后端一般不直接confirm，但以防万一
          console.log('Payment (new card) reported as succeeded by backend upon PI creation.');
          const successMsg = getSuccessMessage(orderData);
          setPaymentStatus({ stage: 'success', message: successMsg });
          setCheckoutSuccess(successMsg);
          if (onSuccess) onSuccess({ ...orderData, paymentIntent: { status: 'succeeded', id: backendResponseData.stripePaymentIntentId } });
        }
        return;
      }

      // 如果状态是 requires_action 或 requires_payment_method, 优先处理SCA
      // if (initialPaymentIntentStatus === 'requires_action' || initialPaymentIntentStatus === 'requires_payment_method') {
      if (initialPaymentIntentStatus && ['requires_action', 'requires_source_action', 'requires_authentication'].includes(initialPaymentIntentStatus)) {
        if (isMounted.current) { // <--- 检查
          console.log(`Payment (new card) requires action (${initialPaymentIntentStatus}) based on backend PI.`);
          setPaymentStatus({ stage: 'handling_action', message: 'Additional verification required, please follow the prompts...' });
        }
        if (onRequiresAction) onRequiresAction({ client_secret: clientSecret, status: initialPaymentIntentStatus });

        if (ENABLE_SCA_HANDLING) {
          const actionResult = await stripe.handleCardAction(clientSecret);
          if (isMounted.current) { // <--- 检查
            console.log('Stripe handleCardAction result (new card):', actionResult);
            if (actionResult.error) {
              throw actionResult.error;
            } else if (actionResult.paymentIntent && actionResult.paymentIntent.status === 'succeeded') {
              const successMsg = getSuccessMessage(orderData);
              setPaymentStatus({ stage: 'success', message: successMsg });
              setCheckoutSuccess(successMsg);
              if (onSuccess) onSuccess({ ...orderData, ...actionResult.paymentIntent });
            } else {
              throw new Error(`SCA payment status not successful: ${actionResult.paymentIntent?.status || 'unknown'}`);
            }
          }
        } else {
          throw new Error('Payment requires SCA but handling is disabled.');
        }
        return;
      }

      // ----- Step 3: Confirm Card Payment with Stripe.js -----
      if (isMounted.current) {
        setPaymentStatus({ stage: 'confirming', message: 'Confirming your payment...' });
        console.log('使用 clientSecret 确认支付 (新卡)...');
      }

      const confirmResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId,
      });

      console.log('Stripe confirmCardPayment result (新卡):', confirmResult);

      // ----- Step 4: Handle Confirmation Result -----
      if (isMounted.current) {
        if (confirmResult.error) {
          console.error('[StripeCDKCheckoutForm] confirmCardPayment ERROR:', confirmResult.error);

          // ****** 新增 CAPTCHA 处理 ******
          if (confirmResult.error.code === 'captcha_challenge') {
            console.log('CAPTCHA challenge required by Stripe.');
            if (isMounted.current) {
              setPaymentStatus({
                stage: 'captcha_required',
                message: 'Please complete the security check to proceed with your payment.'
              });
            }
            throw confirmResult.error;
          }
          // ****** 结束 CAPTCHA 处理 ******
          else if (
            confirmResult.error.code === 'payment_intent_authentication_failure' ||
            confirmResult.error.code === 'authentication_required' ||
            (confirmResult.paymentIntent && ['requires_action', 'requires_source_action'].includes(confirmResult.paymentIntent.status))
          ) {
            if (isMounted.current) {
              console.log('Payment (new card) requires action (SCA/3D Secure) after confirmCardPayment.');
              setPaymentStatus({ stage: 'handling_action', message: 'Additional verification required, please follow the prompts...' });
            }
            const scaClientSecret = confirmResult.paymentIntent?.client_secret || clientSecret;
            if (onRequiresAction) onRequiresAction(confirmResult.paymentIntent || { client_secret: scaClientSecret });

            if (ENABLE_SCA_HANDLING) {
              const actionResult = await stripe.handleCardAction(scaClientSecret);
              if (isMounted.current) {
                console.log('[StripeCDKCheckoutForm] handleCardAction result after confirm error:', actionResult);
                if (actionResult.error) {
                  throw actionResult.error;
                } else if (actionResult.paymentIntent && actionResult.paymentIntent.status === 'succeeded') {
                  console.log('[StripeCDKCheckoutForm] Payment SUCCEEDED after SCA. Triggering onSuccess.');
                  const successMsg = getSuccessMessage(orderData);
                  setPaymentStatus({ stage: 'success', message: successMsg });
                  setCheckoutSuccess(successMsg);
                  if (onSuccess) onSuccess({ ...orderData, ...actionResult.paymentIntent });
                } else {
                  console.error('[StripeCDKCheckoutForm] Payment FAILED after SCA. Status:', actionResult.paymentIntent?.status);
                  throw new Error(`SCA payment status not successful: ${actionResult.paymentIntent?.status || 'unknown'}`);
                }
              }
            } else {
              throw new Error('Payment requires SCA but handling is disabled.');
            }
          } else {
            console.error('[StripeCDKCheckoutForm] ConfirmCardPayment failed with non-SCA error.');
            throw confirmResult.error;
          }
        } else if (confirmResult.paymentIntent && confirmResult.paymentIntent.status === 'succeeded') {
          // 支付直接成功
          console.log('[StripeCDKCheckoutForm] confirmCardPayment SUCCEEDED directly. Triggering onSuccess.'); // Added log
          if (isMounted.current) { // <--- 嵌套检查
            console.log('Payment (new card) succeeded after client-side confirmation.');
            const successMsg = getSuccessMessage(orderData);
            setPaymentStatus({ stage: 'success', message: successMsg });
            setCheckoutSuccess(successMsg);
            if (onSuccess) onSuccess({ ...orderData, ...confirmResult.paymentIntent });
          }
        } else {
          // 确认后出现意外状态
          const status = confirmResult.paymentIntent?.status || 'unknown';
          console.error(`[StripeCDKCheckoutForm] Payment FAILED after confirmCardPayment. Unexpected Status: ${status}`); // Added log
          throw new Error(`Payment processing exception: Payment status not finalized (new card): ${status}. Please check your order or contact support.`);
        }
      }

    } catch (error) {
      if (isMounted.current) {
        console.error('Payment processing exception:', error);
        const errMsg = error.message || (error.error && error.error.message) || 'An error occurred during payment processing, please try again';

        let displayMessage = errMsg;
        if (error.code === 'captcha_challenge' || (error.message && error.message.toLowerCase().includes('captcha'))) {
          displayMessage = 'Failed to complete the security check. Please try again.';
        }

        setPaymentStatus({ stage: 'failed', message: displayMessage });
        setCheckoutError(displayMessage);
        if (onError) onError(error);
      }
    }
  };

  // Helper to determine success message based on backend response
  const getSuccessMessage = (orderData) => {
    // 优先使用后端返回的成功消息
    if (orderData && orderData.successMessage) {
      return orderData.successMessage;
    }

    // 如果没有后端返回的成功消息，则根据订单状态返回适当的消息
    if (!orderData) return 'Payment processed successfully, please check your order later.';

    // 根据订单状态返回适当的消息
    switch (orderData.deliveryStatus) {
      case 'delayed':
        return 'Payment successful! Due to security checks, your CDKey will be sent after a brief delay.';
      case 'manual_review':
        return 'Payment successful! Your order requires manual review and will be sent as soon as possible.';
      case 'sent': // Assuming 'sent' might be returned if processed instantly by webhook
      case 'pending': // Or if webhook hasn't run yet
      default:
        return 'Payment successful! Your CDKey will be sent shortly.';
    }
  };

  // 监听卡号变化以识别卡品牌
  const handleCardChange = (event) => {
    if (event.empty) {
      setCardNumberComplete(false);
      setDisplayCardNumber('•••• •••• •••• ••••');
    } else {
      if (event.complete) {
        setCardNumberComplete(true);
        // 这里只是模拟显示，实际不存储真实卡号
        setDisplayCardNumber('•••• •••• •••• ' + event.value.slice(-4));
      }

      if (event.error) {
        setCardNumberError(true);
      } else {
        setCardNumberError(false);
      }

      // 识别卡品牌
      if (event.brand) {
        setCardBrand(event.brand);
      }
    }
  };

  const handleExpiryChange = (event) => {
    if (event.empty) {
      setCardExpiryComplete(false);
      setDisplayCardExpiry('••/••');
    } else {
      if (event.complete) {
        setCardExpiryComplete(true);
        // 获取过期日期格式
        if (event.value) {
          const parts = event.value.split('/');
          if (parts.length === 2) {
            setDisplayCardExpiry(`${parts[0]}/${parts[1].substr(-2)}`);
          }
        }
      }

      if (event.error) {
        setCardExpiryError(true);
      } else {
        setCardExpiryError(false);
      }
    }
  };

  const handleCvcChange = (event) => {
    if (event.empty) {
      setCardCvcComplete(false);
    } else {
      setCardCvcComplete(event.complete);

      if (event.error) {
        setCardCvcError(true);
      } else {
        setCardCvcError(false);
      }
    }
  };

  const isFormComplete = useMemo(() => {
    if (selectedCard) {
      return agreeToTerms;
    }

    return cardNumberComplete &&
      cardExpiryComplete &&
      cardCvcComplete &&
      nameOnCard.trim() !== '' &&
      agreeToTerms;
  }, [selectedCard, cardNumberComplete, cardExpiryComplete, cardCvcComplete, nameOnCard, agreeToTerms]);

  const selectCard = (cardId) => {
    setSelectedCard(cardId);
    setShowNewCardForm(false);
  };

  const showAddNewCard = () => {
    setSelectedCard(null);
    setShowNewCardForm(true);
  };

  return (
    <form onSubmit={handleSubmit} className={classes.formContainer}>
      {/* 关闭按钮 */}
      {/*{onClose && (
        <div className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </div>
      )}*/}

      {/* 使用StripeCDKSavedCards组件显示已保存的卡 */}
      {loadingSavedCards ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ marginLeft: '10px', color: '#cdccd0' }}>
            Loading saved payment methods...
          </Typography>
        </div>
      ) : savedCards.length > 0 ? (
        <div>
          <StripeCDKSavedCards
            onSelectPaymentMethod={selectCard}
            selectedPaymentMethodId={selectedCard}
            onSetDefault={() => { }}
            onDelete={() => fetchSavedCards()}
            authToken={authToken}
          />

          {/* 添加新卡按钮 */}
          {!showNewCardForm && (
            <div
              className={classes.newCardOption}
              onClick={showAddNewCard}
            >
              <span className={classes.addIcon}>+</span>
              <Typography variant="body1" style={{ color: '#fff' }}>
                Add New Card
              </Typography>
            </div>
          )}
        </div>
      ) : null}

      {/* 新卡表单 */}
      {showNewCardForm && (
        <>
          {/* 卡片预览 */}
          <div className={classes.cardPreview}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div className={classes.cardChip}></div>
              <div className={classes.cardBrand}>
                {cardBrand && (
                  <img
                    src={getCardBrandIcon(cardBrand)}
                    alt={cardBrand}
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                )}
              </div>

            </div>

            <div className={classes.cardNumberPreview}>
              {displayCardNumber}
            </div>

            <div className={classes.cardHolderDetails}>
              <div>
                <div style={{ fontSize: '10px', marginBottom: '5px' }}>CARD HOLDER</div>
                <div>{nameOnCard || 'Your Name'}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', marginBottom: '5px' }}>EXPIRES</div>
                <div>{displayCardExpiry}</div>
              </div>
            </div>
          </div>

          {/* 卡信息表单 */}
          <div>
            <label className={classes.cardNumberLabel}>Card Number</label>
            <div
              className={`${classes.cardContainer} ${cardNumberFocused ? classes.cardFocused : ''} ${cardNumberError ? classes.cardError : ''}`}
              tabIndex={0} // 添加tabIndex提高键盘可访问性
              onClick={() => {
                // 当容器被点击时，尝试聚焦到卡号输入元素
                const cardNumberElement = elements?.getElement(CardNumberElement);
                if (cardNumberElement) {
                  cardNumberElement.focus();
                }
              }}
            >
              <div className={classes.cardContent} style={{ cursor: 'text', position: 'relative', zIndex: 1 }}>
                <div className="card-icon" style={{ position: 'absolute', zIndex: 2, left: 0 }}>
                  {cardBrand ? (
                    <img
                      src={getCardBrandIcon(cardBrand)}
                      alt={cardBrand}
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  ) : (
                    <img
                      src={getCardBrandIcon('generic')}
                      alt="card"
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  )}
                </div>
                <div style={{ width: '100%', paddingLeft: '50px', position: 'relative', zIndex: 3 }}>
                  <CardNumberElement
                    options={{
                      ...cardElementOptions,
                      style: {
                        ...cardElementOptions.style,
                        base: {
                          ...cardElementOptions.style.base,
                          padding: '0 10px',
                          '::placeholder': {
                            color: '#aaa',
                          }
                        }
                      }
                    }}
                    className="stripe-card-number-element"
                    onChange={handleCardChange}
                    onFocus={() => setCardNumberFocused(true)}
                    onBlur={() => setCardNumberFocused(false)}
                  />
                </div>
              </div>
            </div>

            <div className={classes.fieldRow}>
              <div className={classes.halfWidth}>
                <label>Expiration Date</label>
                <div
                  className={`${classes.cardContainer} ${cardExpiryFocused ? classes.cardFocused : ''} ${cardExpiryError ? classes.cardError : ''}`}
                >
                  <CardExpiryElement
                    options={cardElementOptions}
                    onChange={handleExpiryChange}
                    onFocus={() => setCardExpiryFocused(true)}
                    onBlur={() => setCardExpiryFocused(false)}
                  />
                </div>
              </div>

              <div className={classes.halfWidth}>
                <label>CVC</label>
                <div
                  className={`${classes.cardContainer} ${cardCvcFocused ? classes.cardFocused : ''} ${cardCvcError ? classes.cardError : ''}`}
                >
                  <CardCvcElement
                    options={cardElementOptions}
                    onChange={handleCvcChange}
                    onFocus={() => setCardCvcFocused(true)}
                    onBlur={() => setCardCvcFocused(false)}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <label>Name on Card</label>
              <div className={classes.cardContainer}>
                <input
                  type="text"
                  value={nameOnCard}
                  onChange={(e) => setNameOnCard(e.target.value)}
                  placeholder="Name as it appears on your card"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    width: '100%',
                    color: 'white',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                  style={{ color: '#6c63ff' }}
                />
              }
              label="Save my card for future purchases"
              className={classes.saveCardOption}
            />
          </div>
        </>
      )}

      {/* 订单摘要 */}
      <div className={classes.orderSummary}>
        <Typography variant="h6" style={{ marginBottom: '15px', color: '#fff' }}>
          Order Summary
        </Typography>

        <div className={classes.summaryRow}>
          <span>Product:</span>
          <span>{order?.productName}</span>
        </div>

        <div className={classes.summaryRow}>
          <span>Quantity:</span>
          <span>{quantity}</span>
        </div>

        <div className={classes.summaryRow}>
          <span>Price per item:</span>
          <span>{getCurCurrencyOfUnit(currency, order?.salePrice)}</span>
        </div>

        <div className={classes.totalRow}>
          <span>Total Amount:</span>
          <span>{getCurCurrencyOfUnit(currency, amount)}</span>
        </div>
      </div>

      <FormControlLabel
        control={
          <Checkbox
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
            style={{ color: '#6c63ff' }}
          />
        }
        label={
          <span>
            I agree to the <a href="/terms" target="_blank" className={classes.termsLink}>Terms of Service</a> and <a href="/privacy" target="_blank" className={classes.termsLink}>Privacy Policy</a>
          </span>
        }
        className={classes.termsCheckbox}
      />

      {/* Error display */}
      {checkoutError && paymentStatus.stage !== 'failed' && (
        <div className={classes.errorMessage}>{checkoutError}</div>
      )}
      {/* Success display */}
      {checkoutSuccess && paymentStatus.stage !== 'success' && (
        <div className={classes.successMessage}>{checkoutSuccess}</div>
      )}
      {/* Status Indicator will show processing/confirming/failed/success */}
      <PaymentStatusIndicator status={paymentStatus} />

      <Button
        type="submit"
        disabled={!stripe || isProcessing || !isFormComplete || paymentStatus.stage === 'success'} // Disable if already successful
        className={classes.submitButton}
        fullWidth
      >
        {isProcessing ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Pay ${getCurCurrencyOfUnit(currency, amount)}`
        )}
      </Button>

      <Typography variant="body2" style={{ marginTop: '15px', textAlign: 'center', color: '#8a8794' }}>
        {/* Your payment is secured with 256-bit SSL encryption*/}
      </Typography>

      {/* 使用新的测试面板组件替换旧的内联面板 */}
      {process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'staging' && TestPanel && (
        <TestPanel
          stripe={stripe}
          elements={elements}
          options={{
            amount,
            currency,
            productId,
            quantity,
            purchaseMethod,
            giftEmail,
            handlePayment: processPayment,
            handlePaymentWithSavedCard,
            savedCards,
            nameOnCard
          }}
        />
      )}
    </form>
  );
};

export default StripeCDKCheckoutForm;
